import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X, Command } from "lucide-react";
import { Logo } from "@/components/Logo";
import { XLink, CAButton, BuyButton, ChartButton } from "@/components/CryptoBar";
import { LiveAgents } from "@/components/LiveAgents";
import { NAV_LINKS } from "@/lib/constants";
import { useAgents } from "@/context/AgentContext";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { setPaletteOpen } = useAgents();
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className={cn("fixed inset-x-0 top-0 z-50 transition-all duration-300", scrolled ? "border-b border-white/10 bg-[#050814]/80 backdrop-blur-xl" : "bg-transparent")}>
      <nav className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Logo imgClassName="h-8 w-auto" />
          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => cn(
                  "whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "text-cyan-300" : "text-slate-300 hover:text-white"
                )}
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="open-command-palette"
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-slate-200"
          >
            <Search className="h-3.5 w-3.5" /> Search
            <span className="ml-1 inline-flex items-center gap-0.5 rounded border border-white/10 px-1 font-mono text-[10px]"><Command className="h-2.5 w-2.5" />K</span>
          </button>
          <XLink />
          <CAButton />
          <BuyButton />
          <ChartButton />
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 p-2 text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-[#050814]/95 backdrop-blur-xl lg:hidden">
          <div className="space-y-1 px-4 py-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => cn("block rounded-lg px-3 py-2.5 text-sm font-medium", isActive ? "bg-white/5 text-cyan-300" : "text-slate-300")}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-3">
              <XLink /><CAButton />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <BuyButton className="flex-1" /><ChartButton className="flex-1" />
            </div>
            <div className="pt-3"><LiveAgents variant="compact" /></div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
